import { ChangeDetectionStrategy, Component, OnInit, Signal, effect, inject, input, output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ComponentsModule } from '@nx-customer-apps/components';
import { SharedUiModule } from '@nx-customer-apps/shared/ui';
import { RoomType, Level } from '../../models';
import { LevelsService } from '../../services';
import { LevelNamePipe } from '../../pipes';

export type RoomData = {
    type: RoomType;
    levelId: string;
    area: number;
    height: number;
    isCooled: boolean;
    isHeated: boolean;
};

@Component({
    selector: 'rbr-room-editor',
    templateUrl: './room-editor.component.html',
    styleUrls: ['./room-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslatePipe, LevelNamePipe, ComponentsModule, SharedUiModule]
})
export class RoomEditorComponent implements OnInit {
    readonly areaMin = 0;
    // TODO: Adjust max area validator based on the maximum allowed area in the RbR
    readonly areaMax = 100;
    readonly heightMin = 0;
    // TODO: Adjust max height validator based on the maximum allowed height in the RbR
    readonly heightMax = 5;

    headerText = input.required<string>();
    roomData = input.required<RoomData>();
    levelId = input.required<string>();
    changed = output<RoomData>();
    levels: Signal<Level[]>;

    readonly form = new FormGroup({
        type: new FormControl<RoomType | null>(null, {
            validators: [Validators.required]
        }),
        levelId: new FormControl<string | null>(null, {
            validators: [Validators.required]
        }),
        area: new FormControl<number | null>(null, {
            validators: [Validators.required, Validators.min(this.areaMin), Validators.max(this.areaMax)]
        }),
        height: new FormControl<number | null>(null, {
            validators: [Validators.required, Validators.min(this.heightMin), Validators.max(this.heightMax)]
        }),
        isCooled: new FormControl<boolean>(false, {
            nonNullable: true
        }),
        isHeated: new FormControl<boolean>(false, {
            nonNullable: true
        })
    });

    readonly roomTypes = Object.values(RoomType);

    private readonly levelsService = inject(LevelsService);
    private readonly roomDataEffect = effect(() => {
        const data = this.roomData();
        if (!data) {
            return;
        }

        this.form.patchValue({
            type: data.type,
            area: data.area,
            height: data.height,
            isCooled: data.isCooled,
            isHeated: data.isHeated
        });
    });

    private readonly levelEffect = effect(() => {
        const levelId = this.levelId();
        if (!levelId) {
            return;
        }

        this.form.patchValue({ levelId });
    });

    ngOnInit() {
        this.levels = this.levelsService.getLevels();
    }

    onVisibleChange(isVisible: boolean): void {
        if (isVisible) {
            return;
        }

        this.resetFormToInputs();
    }

    onConfirm(drawer: { close: () => void }): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const value = this.form.getRawValue();
        if (!value.type || !value.levelId) {
            return;
        }

        const formValue: RoomData = {
            type: value.type,
            levelId: this.levelId(),
            area: Number(value.area),
            height: Number(value.height),
            isCooled: value.isCooled,
            isHeated: value.isHeated
        };

        console.log('Emitting room editor form value:', formValue);
        this.changed.emit(formValue);
        drawer.close();
    }

    private resetFormToInputs(): void {
        const data = this.roomData();
        const levelId = this.levelId();

        this.form.reset(
            {
                type: data?.type ?? null,
                levelId: levelId ?? levelId ?? null,
                area: data?.area ?? null,
                height: data?.height ?? null,
                isCooled: data?.isCooled ?? false,
                isHeated: data?.isHeated ?? false
            },
            { emitEvent: false }
        );
        this.form.markAsPristine();
        this.form.markAsUntouched();
    }
}
